import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Loading,
  PageHeader,
  Pager,
} from '../App';
import {
  adminApi,
  dateTime,
  errorMessage,
  money,
  type AdminBranch,
  type AdminBusiness,
  type AdminBusinessDetail,
  type AdminStaff,
  type PagedResult,
} from '../api/admin';
import { EmptyState, ErrorState } from '../components/States';

export function BusinessDirectoryPage() {
  const [data, setData] = useState<PagedResult<AdminBusiness> | null>(null);
  const [keyword, setKeyword] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await adminApi.businesses({ keyword: search || undefined, page, pageSize: 25 }));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [search, page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <PageHeader
        title="Businesses"
        description="Platform-wide business directory with real branch, staff and multi-branch entitlement status."
      />
      {error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Alert message={error} />
          <div className="admin-card mb-3">
            <form
              className="row g-2 align-items-end"
              onSubmit={(event) => {
                event.preventDefault();
                setPage(1);
                setSearch(keyword.trim());
              }}
            >
              <div className="col-12 col-md-8">
                <label className="form-label small mb-1" htmlFor="business-search">
                  Search businesses
                </label>
                <input
                  id="business-search"
                  className="form-control form-control-sm"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="Business, slug, owner or identifier"
                />
              </div>
              <div className="col-auto">
                <button className="btn btn-sm btn-primary" type="submit">Search</button>
              </div>
            </form>
          </div>
          {!data ? (
            <Loading rows={6} />
          ) : data.items.length === 0 ? (
            <EmptyState title="No businesses match this search." />
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Business</th>
                      <th>Owner</th>
                      <th>Status</th>
                      <th>Branches</th>
                      <th>Staff accounts</th>
                      <th>Multi-branch entitlement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((business) => (
                      <tr key={business.id}>
                        <td>
                          <Link to={`/businesses/${business.id}`} className="fw-semibold">
                            {business.name}
                          </Link>
                          <div className="text-muted small">{business.id}</div>
                          <div className="text-muted small">{business.slug}</div>
                        </td>
                        <td>
                          <div>{business.ownerName}</div>
                          <div className="text-muted small">{business.ownerEmail}</div>
                        </td>
                        <td>
                          <span className={`badge ${business.isArchived ? 'bg-secondary' : business.isVerified ? 'bg-success' : 'bg-warning text-dark'}`}>
                            {business.status}
                          </span>
                        </td>
                        <td>{business.branchCount}</td>
                        <td>
                          {business.activeStaffAccountCount} active / {business.staffAccountCount} total
                        </td>
                        <td>
                          <div>{business.multiBranchEntitlementStatus}</div>
                          <div className="text-muted small">
                            {business.multiBranchFeatureEnabled ? 'Feature enabled' : 'Feature disabled'}
                            {' · '}
                            {business.multiBranchPaymentRequired ? 'Payment required' : 'Payment not required'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager result={data} onPage={setPage} />
            </>
          )}
        </>
      )}
    </>
  );
}

export function BusinessDetailPage() {
  const { businessId = '' } = useParams();
  const [data, setData] = useState<AdminBusinessDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await adminApi.business(businessId));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !data) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={8} />;

  const business = data.business;
  return (
    <>
      <PageHeader
        title={business.name}
        description={`Business ${business.id} · ${business.status}`}
        actions={<Link className="btn btn-sm btn-outline-secondary" to="/businesses">Business directory</Link>}
      />
      <Alert message={error} />
      <div className="admin-card mb-3">
        <div className="row g-3">
          <Info label="Owner" value={`${business.ownerName} · ${business.ownerEmail}`} />
          <Info label="Contact" value={`${data.contactPersonName ?? '—'} · ${data.contactEmail ?? data.contactPhone}`} />
          <Info label="Branches" value={`${business.branchCount}`} />
          <Info label="Staff accounts" value={`${business.activeStaffAccountCount} active / ${business.staffAccountCount} total`} />
          <Info label="Business status" value={business.status} />
          <Info
            label="Global multi-branch setting"
            value={`${business.multiBranchFeatureEnabled ? 'Feature enabled' : 'Feature disabled'} · ${business.multiBranchPaymentRequired ? 'Payment required' : 'Payment not required'}`}
          />
          <Info label="Current entitlement" value={`${business.multiBranchEntitlementStatus}${business.multiBranchEntitlementExpiryUtc ? ` · expires ${dateTime(business.multiBranchEntitlementExpiryUtc)}` : ''}`} />
        </div>
        {data.description && <p className="text-muted small mt-3 mb-0">{data.description}</p>}
        <div className="text-muted small mt-2">
          Global feature and payment settings do not grant an entitlement. The entitlement status above is business-specific.
        </div>
      </div>

      <PageHeader title="Branches and assigned staff" />
      {data.branches.length === 0 ? (
        <EmptyState title="No branches or listings are recorded for this business." />
      ) : data.branches.map((branch) => (
        <BranchCard key={branch.id} branch={branch} />
      ))}

      <PageHeader title="Multi-branch entitlement history" />
      {data.entitlements.length === 0 ? (
        <div className="admin-card text-muted mb-3">No entitlement records are recorded.</div>
      ) : (
        <div className="admin-table-wrap mb-3">
          <table className="admin-table">
            <thead><tr><th>Current / stored status</th><th>Source</th><th>Period</th><th>Amount</th><th>Permits now</th></tr></thead>
            <tbody>
              {data.entitlements.map((entitlement) => (
                <tr key={entitlement.id}>
                  <td>{entitlement.currentStatus} <span className="text-muted small">(stored: {entitlement.storedStatus})</span></td>
                  <td>{entitlement.source}</td>
                  <td>{dateTime(entitlement.startUtc)} – {dateTime(entitlement.expiryUtc)}{entitlement.graceEndsUtc ? ` · grace to ${dateTime(entitlement.graceEndsUtc)}` : ''}</td>
                  <td>{money(entitlement.totalAmount)} {entitlement.currency}</td>
                  <td>{entitlement.permitsMultipleBranchesNow ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function BranchCard({ branch }: { branch: AdminBranch }) {
  return (
    <div className="admin-card mb-3">
      <div className="d-flex flex-wrap justify-content-between gap-2">
        <div>
          <h2 className="h6 mb-1">
            <Link to={`/branches/${branch.id}`}>{branch.name}</Link>
          </h2>
          <div className="text-muted small">{branch.category} · {branch.locality}, {branch.city} · {branch.status}{branch.isArchived ? ' · Archived' : ''}</div>
          <div className="text-muted small">{branch.address}</div>
        </div>
        <Link className="btn btn-sm btn-outline-primary align-self-start" to={`/branches/${branch.id}`}>Branch details</Link>
      </div>
      <div className="table-responsive mt-3">
        <table className="admin-table">
          <thead><tr><th>Staff account</th><th>Account</th><th>Membership</th><th>Assignment</th><th>Assigned / removed</th></tr></thead>
          <tbody>
            {branch.staff.length === 0 ? (
              <tr><td colSpan={5} className="text-muted">No staff assignments are recorded for this branch.</td></tr>
            ) : branch.staff.map((staff) => (
              <tr key={staff.assignmentId}>
                <td><Link to={`/staff/${staff.userId}`}>{staff.username} · {staff.fullName}</Link></td>
                <td>{staff.accountStatus}</td>
                <td>{staff.membershipIsActive ? 'Active' : 'Inactive'}</td>
                <td>{staff.assignmentIsActive ? 'Active' : 'Removed'}</td>
                <td>{dateTime(staff.assignedAtUtc)}{staff.removedAtUtc ? ` · ${dateTime(staff.removedAtUtc)}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="small mt-3">
        <strong>Branch subscriptions:</strong>{' '}
        {branch.subscriptions.length === 0
          ? 'No subscription records.'
          : branch.subscriptions.map((subscription) =>
              `${subscription.currentStatus} (stored ${subscription.storedStatus}) · ${subscription.planCode} · ${money(subscription.totalAmount)} ${subscription.currency} through ${dateTime(subscription.expiryUtc)}`,
            ).join(' | ')}
      </div>
    </div>
  );
}

export function BranchDetailPage() {
  const { branchId = '' } = useParams();
  const [branch, setBranch] = useState<AdminBranch | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setBranch(await adminApi.branch(branchId));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [branchId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !branch) return <ErrorState message={error} onRetry={load} />;
  if (!branch) return <Loading rows={6} />;

  return (
    <>
      <PageHeader
        title={branch.name}
        description={`${branch.category} · ${branch.locality}, ${branch.city} · ${branch.status}`}
        actions={<Link className="btn btn-sm btn-outline-secondary" to={`/businesses/${branch.businessId}`}>Parent business</Link>}
      />
      <Alert message={error} />
      <div className="admin-card mb-3">
        <div><strong>Business:</strong> <Link to={`/businesses/${branch.businessId}`}>{branch.businessName}</Link></div>
        <div><strong>Address:</strong> {branch.address}</div>
        {branch.statusReason && <div><strong>Status reason:</strong> {branch.statusReason}</div>}
        <div><strong>Archived:</strong> {branch.isArchived ? 'Yes' : 'No'}</div>
      </div>
      <PageHeader title="Staff assignments" />
      <div className="admin-table-wrap mb-3">
        <table className="admin-table">
          <thead><tr><th>Staff</th><th>Account status</th><th>Membership</th><th>Assignment</th><th>Assigned</th><th>Removed</th></tr></thead>
          <tbody>
            {branch.staff.length === 0 ? (
              <tr><td colSpan={6} className="text-muted">No assignment records.</td></tr>
            ) : branch.staff.map((staff) => (
              <tr key={staff.assignmentId}>
                <td><Link to={`/staff/${staff.userId}`}>{staff.username} · {staff.fullName}</Link></td>
                <td>{staff.accountStatus}</td>
                <td>{staff.membershipIsActive ? 'Active' : 'Inactive'}</td>
                <td>{staff.assignmentIsActive ? 'Active' : 'Removed'}</td>
                <td>{dateTime(staff.assignedAtUtc)}</td>
                <td>{dateTime(staff.removedAtUtc)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PageHeader title="Branch subscription history" />
      {branch.subscriptions.length === 0 ? (
        <div className="admin-card text-muted">No branch subscription records.</div>
      ) : (
        <div className="admin-table-wrap mb-3">
          <table className="admin-table">
            <thead><tr><th>Current / stored status</th><th>Plan</th><th>Period</th><th>Amount</th></tr></thead>
            <tbody>
              {branch.subscriptions.map((subscription) => (
                <tr key={subscription.id}>
                  <td>{subscription.currentStatus} <span className="text-muted small">(stored {subscription.storedStatus})</span></td>
                  <td>{subscription.planCode}</td>
                  <td>{dateTime(subscription.startUtc)} – {dateTime(subscription.expiryUtc)}{subscription.graceEndsUtc ? ` · grace to ${dateTime(subscription.graceEndsUtc)}` : ''}</td>
                  <td>{money(subscription.totalAmount)} {subscription.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function StaffDirectoryPage() {
  const [data, setData] = useState<PagedResult<AdminStaff> | null>(null);
  const [keyword, setKeyword] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await adminApi.staff({ keyword: search || undefined, page, pageSize: 25 }));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [search, page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <PageHeader title="Branch staff" description="Platform-wide staff accounts, business memberships and current or historical branch assignments." />
      {error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Alert message={error} />
          <form
            className="admin-card row g-2 align-items-end mb-3"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setSearch(keyword.trim());
            }}
          >
            <div className="col-12 col-md-8">
              <label className="form-label small mb-1" htmlFor="staff-search">Search staff</label>
              <input
                id="staff-search"
                className="form-control form-control-sm"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="Username, name, email or phone"
              />
            </div>
            <div className="col-auto"><button className="btn btn-sm btn-primary" type="submit">Search</button></div>
          </form>
          {!data ? (
            <Loading rows={6} />
          ) : data.items.length === 0 ? (
            <EmptyState title="No staff accounts match this search." />
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr><th>Account</th><th>Account status</th><th>Business memberships and assignments</th></tr></thead>
                  <tbody>
                    {data.items.map((staff) => (
                      <tr key={staff.userId}>
                        <td>
                          <Link to={`/staff/${staff.userId}`} className="fw-semibold">{staff.fullName}</Link>
                          <div className="text-muted small">{staff.email}</div>
                          {staff.phone && <div className="text-muted small">{staff.phone}</div>}
                          <div className="text-muted small">{staff.userId}</div>
                        </td>
                        <td>{staff.accountStatus}</td>
                        <td>
                          {staff.memberships.map((membership) => (
                            <div key={membership.membershipId} className="mb-2">
                              <Link to={`/businesses/${membership.businessId}`}>{membership.businessName}</Link>
                              <span className="text-muted"> · {membership.username} · membership {membership.isActive ? 'active' : 'inactive'}</span>
                              <div className="small">
                                {membership.assignments.length === 0
                                  ? 'No branch assignment records'
                                  : membership.assignments.map((assignment) => (
                                      <span key={assignment.assignmentId} className="me-2">
                                        <Link to={`/branches/${assignment.branchId}`}>{assignment.branchName}</Link>
                                        {' · '}{assignment.isActive ? 'assigned' : 'removed'}
                                      </span>
                                    ))}
                              </div>
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager result={data} onPage={setPage} />
            </>
          )}
        </>
      )}
    </>
  );
}

export function StaffDetailPage() {
  const { userId = '' } = useParams();
  const [staff, setStaff] = useState<AdminStaff | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setStaff(await adminApi.staffDetail(userId));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !staff) return <ErrorState message={error} onRetry={load} />;
  if (!staff) return <Loading rows={6} />;

  return (
    <>
      <PageHeader
        title={staff.fullName}
        description={`Staff account ${staff.userId} · ${staff.accountStatus}`}
        actions={<Link className="btn btn-sm btn-outline-secondary" to="/staff">Staff directory</Link>}
      />
      <Alert message={error} />
      <div className="admin-card mb-3">
        <div><strong>Email:</strong> {staff.email || '—'}</div>
        <div><strong>Phone:</strong> {staff.phone ?? '—'}</div>
        <div><strong>Account:</strong> {staff.accountStatus}</div>
        <div><strong>Created:</strong> {dateTime(staff.createdAtUtc)}</div>
        <div><strong>Last login:</strong> {dateTime(staff.lastLoginAtUtc)}</div>
      </div>
      <PageHeader title="Business memberships and branch assignments" />
      {staff.memberships.length === 0 ? (
        <EmptyState title="No branch-employee memberships are recorded." />
      ) : staff.memberships.map((membership) => (
        <div className="admin-card mb-3" key={membership.membershipId}>
          <h2 className="h6">
            <Link to={`/businesses/${membership.businessId}`}>{membership.businessName}</Link>
          </h2>
          <div className="small text-muted">
            {membership.username} · {membership.role} · membership {membership.isActive ? 'active' : 'inactive'}
          </div>
          <div className="table-responsive mt-2">
            <table className="admin-table">
              <thead><tr><th>Branch</th><th>Assignment</th><th>Assigned</th><th>Removed</th></tr></thead>
              <tbody>
                {membership.assignments.length === 0 ? (
                  <tr><td colSpan={4} className="text-muted">No branch assignment records.</td></tr>
                ) : membership.assignments.map((assignment) => (
                  <tr key={assignment.assignmentId}>
                    <td><Link to={`/branches/${assignment.branchId}`}>{assignment.branchName}</Link></td>
                    <td>{assignment.isActive ? 'Active' : 'Removed'}</td>
                    <td>{dateTime(assignment.assignedAtUtc)}</td>
                    <td>{dateTime(assignment.removedAtUtc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="col-12 col-md-6 col-xl-4">
      <div className="small text-muted">{label}</div>
      <div>{value}</div>
    </div>
  );
}
